<template>
  <div>
    <div v-if="business" class="flex flex-wrap px-4 mb-2">
      <div class="w-full">
        <span class="text-xs text-blueGray-400">
          Viewing data for <span class="font-semibold text-blueGray-600">{{ business.name }}</span>
        </span>
      </div>
    </div>

    <!-- Balance glance: the business's own dedicated wallet (reads 0 until there's a
         funding flow into it — see card-stats description) plus the cluster total,
         which is the real, populated aggregate of everyone transacting through this
         business. Same tiles/shape as WalletOverview.vue's Wallet page, so the two
         surfaces agree instead of showing different numbers for "the balance". -->
    <div v-if="businessWallets.length" class="mb-2">
      <h6 class="text-blueGray-500 uppercase text-xs font-bold px-4 mt-2 mb-1">Business Wallet</h6>
      <div class="flex flex-wrap px-2">
        <div v-for="wallet in businessWallets" :key="'business-' + wallet.currency" class="w-full lg:w-6/12 xl:w-3/12 px-2 mb-3">
          <card-stats
            :statSubtitle="`${wallet.currency} balance`"
            :statTitle="formatAbbrCurrency({ currency: wallet.currency, amount: wallet.balance })"
            statArrow="up"
            statCount="Business"
            statPercentColor="text-indigo-500"
            statDescripiron="your business's own wallet"
            statIconName="far fa-building"
            statIconColor="bg-blueGray-900"
          />
        </div>
      </div>
    </div>
    <div v-if="clusterWallets.length" class="mb-2">
      <h6 class="text-blueGray-500 uppercase text-xs font-bold px-4 mt-2 mb-1">Cluster Total</h6>
      <div class="flex flex-wrap px-2">
        <div v-for="wallet in clusterWallets" :key="'cluster-' + wallet.currency" class="w-full lg:w-6/12 xl:w-3/12 px-2 mb-3">
          <card-stats
            :statSubtitle="`${wallet.currency} balance`"
            :statTitle="formatAbbrCurrency({ currency: wallet.currency, amount: wallet.balance })"
            statArrow="up"
            :statCount="`${wallet.count} wallets`"
            statPercentColor="text-orange-500"
            statDescripiron="everyone in your cluster"
            statIconName="far fa-chart-bar"
            statIconColor="bg-blueGray-900"
          />
        </div>
      </div>
    </div>

    <!-- Wallet balance is already covered above (Business Wallet / Cluster Total), so
         this row skips duplicating it and only adds the KPIs nothing else on this page
         shows yet. -->
    <div class="flex flex-wrap px-4">
      <div class="w-full lg:w-6/12 xl:w-4/12 px-2 mb-4">
        <card-stats
          statSubtitle="New Users"
          :statTitle="`${kpis.new_users || 0}`"
          statArrow="up"
          :statCount="`${kpis.new_users || 0}`"
          statPercentColor="text-emerald-500"
          statDescripiron="registered in this period"
          statIconName="fas fa-user-plus"
          statIconColor="bg-blueGray-900"
        />
      </div>
      <div class="w-full lg:w-6/12 xl:w-4/12 px-2 mb-4">
        <card-stats
          statSubtitle="Active Subscriptions"
          :statTitle="formatAbbrCurrency({ currency: 'NGN', amount: kpis.active_subscriptions?.total || 0 })"
          statArrow="up"
          :statCount="`${kpis.active_subscriptions?.count || 0}`"
          statPercentColor="text-orange-500"
          statDescripiron="active subscriptions under management"
          statIconName="fas fa-box"
          statIconColor="bg-blueGray-900"
        />
      </div>
      <div class="w-full lg:w-6/12 xl:w-4/12 px-2 mb-4">
        <card-stats
          statSubtitle="Revenue"
          :statTitle="formatAbbrCurrency({ currency: 'NGN', amount: kpis.revenue || 0 })"
          statArrow="up"
          :statCount="formatAbbrCurrency({ currency: 'NGN', amount: kpis.revenue || 0 })"
          statPercentColor="text-emerald-500"
          statDescripiron="commission earned this period"
          statIconName="fas fa-sack-dollar"
          statIconColor="bg-blueGray-900"
        />
      </div>
    </div>

    <div class="flex flex-wrap items-end px-4 mb-4" style="gap: 0.75rem;">
      <div>
        <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">From</label>
        <input
          type="date"
          v-model="range.from"
          class="border border-blueGray-200 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-xs focus:outline-none"
          @input="activePreset = null"
        />
      </div>
      <div>
        <label class="block uppercase text-blueGray-600 text-xs font-bold mb-1">To</label>
        <input
          type="date"
          v-model="range.to"
          class="border border-blueGray-200 px-3 py-2 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-xs focus:outline-none"
          @input="activePreset = null"
        />
      </div>
      <button
        type="button"
        class="bg-blueGray-900 text-white active:bg-blueGray-800 hover:bg-blueGray-800 text-xs px-4 py-2 rounded outline-none focus:outline-none"
        @click="loadDashboard(range)"
      >
        Apply
      </button>
      <div class="flex" style="gap: 0.375rem;">
        <button
          v-for="preset in presets"
          :key="preset.label"
          type="button"
          class="text-xs px-3 py-2 rounded outline-none focus:outline-none"
          :class="activePreset === preset.label
            ? 'bg-blueGray-900 text-white'
            : 'bg-blueGray-200 text-blueGray-600 hover:bg-blueGray-300'"
          @click="applyPreset(preset)"
        >
          {{ preset.label }}
        </button>
      </div>
    </div>

    <div v-if="!loading && noActivity" class="px-4 mb-2">
      <span class="text-xs text-blueGray-400">No activity in this date range.</span>
    </div>

    <div class="flex flex-wrap">
      <div class="w-full xl:w-8/12 mb-12 xl:mb-0 px-4">
        <chart-skeleton v-if="loading" />
        <card-line-chart v-else :data="transactionData" title="Sales Record" />
      </div>
      <div class="w-full xl:w-4/12 px-4">
        <chart-skeleton v-if="loading" />
        <card-bar-chart v-else :data="userData" title="User Registration" />
      </div>
    </div>

    <div class="flex flex-wrap mt-4">
      <div class="w-full xl:w-4/12 px-4">
        <card-attention-list :items="attentionItems" />
      </div>
      <div class="w-full xl:w-4/12 px-4">
        <chart-skeleton v-if="loading" />
        <card-doughnut-chart v-else :data="repaymentHealthData" title="Repayment Health" />
      </div>
      <div class="w-full xl:w-4/12 px-4" v-if="gatewayHealth.length > 0">
        <chart-skeleton v-if="loading" />
        <card-gateway-health-chart v-else :data="gatewayHealthData" title="Gateway Health" />
      </div>
    </div>

    <div class="flex flex-wrap mt-4">
      <div class="w-full mb-12 xl:mb-0 px-4">
        <card-transactions></card-transactions>
      </div>
    </div>
  </div>
</template>
<script>
import CardLineChart from "@/components/Cards/CardLineChart.vue";
import CardBarChart from "@/components/Cards/CardBarChart.vue";
import CardTransactions from "@/components/Cards/CardTransactions.vue";
import CardStats from "@/components/Cards/CardStats.vue";
import CardAttentionList from "@/components/Cards/CardAttentionList.vue";
import CardDoughnutChart from "@/components/Cards/CardDoughnutChart.vue";
import CardGatewayHealthChart from "@/components/Cards/CardGatewayHealthChart.vue";
import ChartSkeleton from "@/components/Cards/ChartSkeleton.vue";
import { mapActions } from 'vuex';
import { formatAbbrCurrency } from "../../utils/formatter.util";

// Compact "Mon, 12 May" style label from a plain Y-m-d date string — avoids pulling in
// a date library just for this; the backend already returns one point per calendar day
// in the range, in order, so we just need to label each with its own real date instead
// of assuming a fixed Monday-first week (which broke as soon as "today" wasn't aligned
// to that, or a day in the range had zero activity).
function formatChartDate(dateStr) {
  const date = new Date(`${dateStr}T00:00:00`);
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

export default {
  name: "dashboard-page",
  components: {
    CardLineChart,
    CardBarChart,
    CardTransactions,
    CardStats,
    CardAttentionList,
    CardDoughnutChart,
    CardGatewayHealthChart,
    ChartSkeleton
  },
  data() {
    return {
      userData: {},
      transactionData: {},
      business: null,
      businessWallets: [],
      clusterWallets: [],
      range: { from: '', to: '' },
      loading: true,
      noActivity: false,
      kpis: {},
      attentionItems: [],
      repaymentHealthData: {},
      gatewayHealth: [],
      gatewayHealthData: {},
      activePreset: null,
      presets: [
        { label: 'Today', days: 1 },
        { label: '7D', days: 7 },
        { label: '30D', days: 30 },
      ]
    }
  },
  created() {
    this.formatAbbrCurrency = formatAbbrCurrency;
  },
  mounted(){
    this.loadDashboard({});
    this.getWalletOverview().then(overview => {
      const businessSection = overview && overview.business;
      if (businessSection) {
        this.businessWallets = Object.values(businessSection)
          .filter(Boolean)
          .map(wallet => ({ currency: wallet.base_currency, balance: wallet.balance }));
      }

      // Cluster aggregate — { naira: [{total_balance, count, ...}], usd: [...] } — the
      // one number here that's actually populated with real money today, since nothing
      // yet funds a business's own wallet directly (see businessWallets above).
      const clusterSection = overview && overview.cluster;
      if (clusterSection) {
        this.clusterWallets = Object.entries(clusterSection)
          .filter(([, stat]) => stat && stat[0])
          .map(([currency, stat]) => ({
            currency: currency.toUpperCase(),
            balance: stat[0].total_balance || 0,
            count: stat[0].count || 0
          }));
      }
    }).catch(err => {
      console.error("Failed to load wallet overview", err);
    });
  },
  methods: {
    ...mapActions(["getDashboard", "getWalletOverview"]),
    // Today/7D/30D shortcuts for the date-range inputs — sets the range and applies it
    // immediately, rather than making the user also click Apply after picking a preset.
    applyPreset(preset) {
      const toDate = new Date();
      const fromDate = new Date();
      fromDate.setDate(toDate.getDate() - (preset.days - 1));
      const toIso = d => d.toISOString().slice(0, 10);
      this.range = { from: toIso(fromDate), to: toIso(toDate) };
      this.activePreset = preset.label;
      this.loadDashboard(this.range);
    },
    loadDashboard(params = {}) {
      this.loading = true;
      this.getDashboard(params).then(res => {
        this.business = res.business || null;
        this.kpis = res.kpis || {};

        const attention = res.attention || {};
        this.attentionItems = [
          {
            label: 'Overdue/defaulted installments',
            count: attention.defaulted_installments || 0,
            icon: 'pi pi-exclamation-triangle',
            route: '/admin/plans'
          },
          {
            label: 'Commissions awaiting payment',
            count: attention.pending_commissions || 0,
            icon: 'pi pi-wallet',
            route: '/admin/wallet'
          },
          // Disputes have no business attribution in the schema, so the backend
          // only includes this count for the unscoped admin view, never a partner's —
          // this branch is here purely for parity with admin's Dashboard.vue and will
          // never actually trigger for this app.
          ...(attention.open_disputes !== undefined ? [{
            label: 'Open disputes',
            count: attention.open_disputes,
            icon: 'pi pi-flag',
            route: '/admin/transactions'
          }] : [])
        ];

        const health = res.repayment_health || { paid: 0, pending: 0, failed: 0 };
        this.repaymentHealthData = {
          labels: ['Paid', 'Pending', 'Failed'],
          datasets: [{
            data: [health.paid, health.pending, health.failed],
            backgroundColor: ['#0f172a', '#94a3b8', '#e11d48'],
          }]
        };

        this.gatewayHealth = res.gateway_health || [];
        this.gatewayHealthData = {
          labels: this.gatewayHealth.map(g => g.provider),
          datasets: [
            {
              label: 'Successful',
              backgroundColor: '#0f172a',
              data: this.gatewayHealth.map(g => g.successful),
            },
            {
              label: 'Failed',
              backgroundColor: '#e11d48',
              data: this.gatewayHealth.map(g => g.failed),
            },
          ]
        };

        const registeredUser = (res.users || []).map(u => u.register);
        const unverifiedUser = (res.users || []).map(u => u.unverified);
        const userLabels = (res.users || []).map(u => formatChartDate(u.date));

        const creditRecord = (res.transactions || []).map(t => t.credit);
        const debitRecord = (res.transactions || []).map(t => t.debit);
        const transactionLabels = (res.transactions || []).map(t => formatChartDate(t.date));

        // Flags a range with literally nothing in it, so the UI can say so instead of
        // silently rendering a flat, empty-looking chart that reads as "broken".
        this.noActivity = [...registeredUser, ...unverifiedUser, ...creditRecord, ...debitRecord]
          .every(v => !v);

        this.userData = {
          labels: userLabels,
          datasets: [
            {
              label: "Unverified",
              backgroundColor: "#e11d48",
              borderColor: "#e11d48",
              data: unverifiedUser,
              fill: false,
              barThickness: 8,
            },
            {
              label: "Registered",
              fill: false,
              backgroundColor: "#0f172a",
              borderColor: "#0f172a",
              data: registeredUser,
              barThickness: 8,
            },
          ],
        };

        this.transactionData = {
          labels: transactionLabels,
          datasets: [
            {
              label: "Credit",
              backgroundColor: "#0f172a",
              borderColor: "#0f172a",
              data: creditRecord,
              fill: false,
            },
            {
              label: "Debit",
              fill: false,
              backgroundColor: "#e11d48",
              borderColor: "#e11d48",
              data: debitRecord,
            },
          ],
        }
      }).finally(() => {
        this.loading = false;
      })
    }
  }
};
</script>
